import clsx from 'clsx';
import { components } from '@mosey/api-types';
import { formatQuestionAnswer } from '../../utils';
import { AnswerBox } from './AnswerBox';
import {
  useAnswers,
  useIsBulkQuestion,
  useQuestionFieldVariant,
} from '../../utils/hooks';

type Field = components['schemas']['Field'];
type RadioParams = components['schemas']['RadioParams'];
type CheckboxParams = { options: components['schemas']['Option'][] };

interface AnsweredFieldProps {
  field: components['schemas']['Field'];
}

export const AnsweredField = ({ field }: AnsweredFieldProps) => {
  const variant = useQuestionFieldVariant();
  const answers = useAnswers();
  const isBulkQuestion = useIsBulkQuestion();

  switch (field.component.type) {
    case 'fieldset':
      return (
        <>
          <p>{field.label}</p>

          <ul className="flex flex-col gap-y-3">
            {(field.component.params as { contents: Field[] }).contents.map(
              (fieldsetField) => (
                <li key={fieldsetField.name}>
                  <AnsweredField field={fieldsetField} />
                </li>
              ),
            )}
          </ul>
        </>
      );

    case 'checkbox':
      return (
        <>
          <p>{field.label}</p>

          <AnswerBox variant={variant}>
            <ul className="space-y-2">
              {(field.component.params as CheckboxParams).options.map(
                (option) => {
                  const answer = answers[option.value];

                  if (answer === 'true') {
                    return <li key={option.value}>{option.name}</li>;
                  }

                  return null;
                },
              )}
            </ul>
          </AnswerBox>
        </>
      );

    case 'radio':
      return (
        <>
          <p>{field.label}</p>

          <AnswerBox variant={variant}>
            {(field.component.params as RadioParams).options.map((option) => {
              if (
                (isBulkQuestion && answers[option.value] === 'true') ||
                answers[field.name] === option.value
              ) {
                return option.name;
              }

              return null;
            })}
          </AnswerBox>
        </>
      );

    default:
      return (
        <div
          className={clsx(
            'flex w-full flex-col',
            variant === 'large' && 'gap-y-6',
            variant === 'medium' &&
              'mt-1 max-w-96 gap-x-6 gap-y-4 @sm/answered-fields:mt-0 @sm/answered-fields:flex-row @sm/answered-fields:items-center @sm/answered-fields:gap-y-2',
          )}
        >
          <p
            className={clsx(
              variant === 'medium' && '@sm/answered-fields:mr-auto',
            )}
          >
            {field.label}
          </p>

          <AnswerBox variant={variant}>
            {formatQuestionAnswer(answers[field.name])}
          </AnswerBox>
        </div>
      );
  }
};
